import React, { useEffect, useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination, Navigation, Autoplay } from 'swiper/modules';
import { ChevronLeft, ChevronRight, Star } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/navigation';

interface Review {
  id: string;
  first_name: string;
  review_text: string;
  rating: number | null;
}

const ReviewsSection = () => {
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchReviews = async () => {
      const { data, error } = await supabase
        .from('reviews')
        .select('*')
        .eq('is_active', true)
        .order('display_order', { ascending: true });

      if (error) {
        console.error('Error fetching reviews:', error);
      } else {
        setReviews(data || []);
      }
      setLoading(false);
    };

    fetchReviews();
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (reviews.length === 0) {
    return null;
  }

  return (
    <div className="py-16">
      <h2 className="text-3xl font-semibold mb-12 text-center">Avis des Voyageurs</h2>
      
      <div className="relative max-w-4xl mx-auto px-4 group">
        <Swiper
          spaceBetween={30}
          slidesPerView={1}
          loop={true}
          pagination={{ 
            clickable: true,
            dynamicBullets: true 
          }}
          navigation={{
            prevEl: '.swiper-button-prev-reviews',
            nextEl: '.swiper-button-next-reviews',
          }}
          autoplay={{
            delay: 5000,
            disableOnInteraction: false,
          }}
          modules={[Pagination, Navigation, Autoplay]}
          className="pb-12"
          breakpoints={{
            768: {
              slidesPerView: 1,
            },
          }}
        >
          {reviews.map((review) => (
            <SwiperSlide key={review.id}>
              <div className="bg-white rounded-lg shadow-lg p-8 md:p-12 min-h-[250px] flex flex-col justify-center">
                {review.rating && (
                  <div className="flex justify-center mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star
                      key={i}
                      strokeWidth={0}
                      className="h-5 w-5"
                      fill={i < review.rating! ? '#86AA8E' : '#E5E7EB'}
                    />
                  ))}
                  </div>
                )}
                
                <blockquote className="text-lg md:text-xl text-gray-700 text-center italic mb-6">
                  "{review.review_text}"
                </blockquote>
                
                <p className="text-center font-semibold text-primary text-lg">
                  - {review.first_name}
                </p>
              </div>
            </SwiperSlide>
          ))}
        </Swiper>

        {/* Navigation Arrows */}
        <button 
          className="swiper-button-prev-reviews hidden md:flex absolute top-1/2 -translate-y-1/2 -left-12 lg:-left-16 z-10 p-3 bg-white hover:bg-gray-50 rounded-full shadow-lg transition-all duration-200 items-center justify-center opacity-0 group-hover:opacity-100"
          aria-label="Avis précédent"
        >
          <ChevronLeft className="h-6 w-6 text-primary" />
        </button>
        
        <button 
          className="swiper-button-next-reviews hidden md:flex absolute top-1/2 -translate-y-1/2 -right-12 lg:-right-16 z-10 p-3 bg-white hover:bg-gray-50 rounded-full shadow-lg transition-all duration-200 items-center justify-center opacity-0 group-hover:opacity-100"
          aria-label="Avis suivant"
        >
          <ChevronRight className="h-6 w-6 text-primary" />
        </button>
      </div>
    </div>
  );
};

export default ReviewsSection;
